import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { LEARN_TOPICS, LEARN_CATEGORIES, type LearnTopicCategory } from "@/data/learnTopics";
import Breadcrumbs from "@/components/seo/Breadcrumbs";

const META_TITLE = "MS Learn Library | Fatigue, Meds, Wearables, Daily Strategies";
const META_DESC = "A growing library of practical, data-informed pieces on managing MS energy: symptoms, medications, wearables, and daily strategies.";

const LearnIndex = () => {
  useEffect(() => {
    document.title = META_TITLE;
    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", META_DESC);
    setMeta("og:title", META_TITLE, "property");
    setMeta("og:description", META_DESC, "property");
    setMeta("og:type", "website", "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://myelinahealth.com/learn";

    // JSON-LD: CollectionPage + ItemList of topics + BreadcrumbList
    const SITE = "https://myelinahealth.com";
    const LEARN_URL = `${SITE}/learn`;
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${LEARN_URL}#collection`,
      name: META_TITLE,
      description: META_DESC,
      url: LEARN_URL,
      inLanguage: "en",
      isPartOf: { "@type": "WebSite", name: "Myelina Health", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Myelina Health",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    };
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "MS Learn Library",
      itemListElement: LEARN_TOPICS.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE}/learn/${t.slug}`,
        name: t.title,
      })),
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Learn", item: LEARN_URL },
      ],
    };
    const nodes: HTMLScriptElement[] = [];
    for (const [id, data] of [
      ["learn-index-jsonld", collectionLd],
      ["learn-index-itemlist-jsonld", itemListLd],
      ["learn-index-breadcrumb-jsonld", breadcrumbLd],
    ] as const) {
      document.getElementById(id)?.remove();
      const s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      nodes.push(s);
    }
    return () => nodes.forEach((n) => n.remove());
  }, []);

  const grouped = useMemo(() => {
    const g: Record<LearnTopicCategory, typeof LEARN_TOPICS> = {
      symptom: [], medication: [], wearable: [], lifestyle: [],
    };
    LEARN_TOPICS.forEach((t) => g[t.category].push(t));
    return g;
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 py-24 max-w-4xl">
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Learn" }]} />
        <div className="mb-12">
          <div className="eyebrow mb-4 inline-flex items-center gap-1.5">
            <Compass className="h-3 w-3" /> Learn library
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">
            Practical, data-informed reading on MS energy.
          </h1>
          <p className="text-[17px] text-muted-foreground max-w-2xl leading-relaxed">
            Written for people managing MS and the clinicians who partner with them. Every piece pairs what the research says with what Myelina Health users actually report.
          </p>
        </div>

        {(Object.keys(grouped) as LearnTopicCategory[]).map((cat) => (
          grouped[cat].length > 0 && (
            <section key={cat} className="mb-12">
              <h2 className="font-heading text-lg font-semibold mb-4">{LEARN_CATEGORIES[cat]}</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {grouped[cat].map((t) => (
                  <Link
                    key={t.slug}
                    to={`/learn/${t.slug}`}
                    className="group block border border-border/60 rounded-2xl p-5 hover:border-primary/40 hover:shadow-sm transition-all"
                  >
                    <p className="font-heading text-[15px] font-semibold mb-2 leading-snug group-hover:text-primary transition-colors">
                      {t.title}
                    </p>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">{t.description}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-[12px] text-primary">
                      Read <ArrowRight className="h-3 w-3" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )
        ))}
      </main>
      <Footer />
    </div>
  );
};

export default LearnIndex;
